import {Link as RouterLink} from 'react-router-dom';
import RequestPageOutlinedIcon from '@mui/icons-material/RequestPageOutlined';
import {Box, IconButton, Link, Tooltip} from '@mui/material';
import isNil from 'lodash/isNil';
import {MRT_ColumnDef} from 'material-react-table';

import {EventLink} from '@entities/event';
import {UserLink} from '@entities/user';
import {CurrencyValue, rightAlignmentColumnProps} from '@shared/components';
import {
  DATETIME_FORMAT,
  ROUTE__EVENT_ORDERS,
  ROUTE__ORDER_FULL_INFO,
} from '@shared/constants';
import {formatInTz} from '@shared/utils';
import {formatOrderEnumValue, Order, OrderType} from '../types';
import {OrderLabelBadge} from './OrderLabelBadge';
import {TriggerBadges} from './TriggerBadges';

const EMPTY_VALUE = '—';

// Orders are identified by the event they belong to plus the ID tail
const formatOrderId = ({id, eventId}: Order): string => {
  const idTail = id.slice(-6);

  return eventId ? `${eventId}-${idTail}` : idTail;
};

// For non-limit orders the matching mode is shown next to the type,
// so the Matching column is not needed
const formatOrderType = ({orderType, matching}: Order): string => {
  const type = formatOrderEnumValue(orderType);

  if (orderType === OrderType.limit || !matching) {
    return type;
  }

  return `${type} (${formatOrderEnumValue(matching)})`;
};

export const orderColumns: MRT_ColumnDef<Order>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    Cell: ({row}) => (
      <Link
        component={RouterLink}
        to={ROUTE__ORDER_FULL_INFO.replace(':id', row.original.id)}
        underline="hover"
      >
        {formatOrderId(row.original)}
      </Link>
    ),
    size: 160,
  },
  {
    accessorKey: 'userId',
    header: 'User',
    Cell: ({row}) => <UserLink userId={row.original.userId} />,
    size: 130,
  },
  {
    accessorKey: 'action',
    header: 'Action',
    Cell: ({row}) => formatOrderEnumValue(row.original.action),
    size: 140,
  },
  {
    accessorKey: 'created',
    header: 'Created',
    Cell: ({row}) => {
      const {created} = row.original;

      return formatInTz(created, DATETIME_FORMAT);
    },
    size: 140,
  },
  {
    accessorKey: 'labels',
    header: 'Labels',
    Cell: ({row}) => (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {row.original.labels.map(label => (
          <OrderLabelBadge key={label} label={label} isShortened />
        ))}
      </Box>
    ),
    // Labels carry no order, so sorting them makes no sense
    enableSorting: false,
    size: 120,
  },
  {
    id: 'triggers',
    header: 'Triggers',
    Cell: ({row}) => (
      <TriggerBadges
        auto_cancel={row.original.auto_cancel}
        stop_loss={row.original.stop_loss}
        take_profit={row.original.take_profit}
      />
    ),
    enableSorting: false,
    size: 160,
  },
  {
    accessorKey: 'eventId',
    header: 'Event ID',
    Cell: ({row}) => {
      const {eventId} = row.original;

      if (!eventId) {
        return null;
      }

      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <EventLink eventId={eventId} />
          <Tooltip title="Orders of this event">
            <IconButton
              size="small"
              component={RouterLink}
              to={ROUTE__EVENT_ORDERS.replace(':event_id', eventId)}
            >
              <RequestPageOutlinedIcon sx={{fontSize: 16}} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    size: 180,
  },
  {
    accessorKey: 'orderType',
    header: 'Type',
    Cell: ({row}) => formatOrderType(row.original),
    size: 180,
  },
  {
    accessorKey: 'choice',
    header: 'Side',
    Cell: ({row}) => formatOrderEnumValue(row.original.choice),
    size: 140,
  },
  {
    accessorKey: 'price',
    header: 'Price',
    Cell: ({row}) => {
      const {price} = row.original;

      return isNil(price) ? EMPTY_VALUE : <CurrencyValue value={price} />;
    },
    size: 140,
    ...rightAlignmentColumnProps,
  },
  {
    accessorKey: 'requestedItemCount',
    header: 'Requested',
    // An order requests either a number of items or an amount to invest
    Cell: ({row}) => {
      const {requestedItemCount, requestedMaxInv} = row.original;

      if (requestedItemCount) {
        return `Qty ${requestedItemCount}`;
      }

      if (requestedMaxInv) {
        return (
          <>
            Amount <CurrencyValue value={requestedMaxInv} />
          </>
        );
      }

      return EMPTY_VALUE;
    },
    size: 160,
  },
  {
    accessorKey: 'matchedQuantity',
    header: 'Matched',
    Cell: ({row}) =>
      `${row.original.matchedQuantity} / ${row.original.exitedQuantity}`,
    size: 140,
  },
];
